import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    ProjectSection,
    ProjectSectionFilter,
    ProjectSectionId,
    ProjectSectionPagination,
    ProjectSectionSort,
} from '../domain/Project';

export const moduleName = 'PROJECT';

type State = {
    resetProjectSectionsOngoing: boolean;
    projectSections: Array<ProjectSection>;
    selectedProjectSectionIds: Array<ProjectSectionId>;
    sort: ProjectSectionSort;
    filter: ProjectSectionFilter;
    pagination: ProjectSectionPagination;
    columnVisibility: Record<string, boolean>;
    currentProjectId: string | null;
};

const initialState: State = {
    resetProjectSectionsOngoing: false,
    projectSections: [],
    selectedProjectSectionIds: [],
    sort: [],
    filter: { items: [] },
    pagination: { page: 0, pageSize: 100 },
    columnVisibility: {
        name: false,
        prd: false,
        abd: false,
        prf: false,
        abf: false,
        gestionary: false,
        trucks: false,
        urban: false,
        roadType: false,
        year: false,
    },
    currentProjectId: null,
};

const projectSlice = createSlice({
    name: moduleName,
    initialState,
    reducers: {
        resetStarted(state: State) {
            state.resetProjectSectionsOngoing = true;
            state.projectSections = [];
            state.selectedProjectSectionIds = [];
        },
        resetWith(
            state: State,
            action: PayloadAction<{
                projectSections: Array<ProjectSection>;
                sort: ProjectSectionSort;
                filter: ProjectSectionFilter;
            }>,
        ) {
            state.sort = action.payload.sort;
            state.filter = action.payload.filter;
            state.projectSections = action.payload.projectSections;
            state.resetProjectSectionsOngoing = false;
            state.selectedProjectSectionIds = [];
        },
        setProject(state: State, action: PayloadAction<Array<ProjectSection>>) {
            state.projectSections = action.payload;
        },
        selectProjectSection(state: State, action: PayloadAction<ProjectSectionId>) {
            if (!state.selectedProjectSectionIds.includes(action.payload)) {
                state.selectedProjectSectionIds.push(action.payload);
            }
        },
        updatedProjectSection(state: State, action: PayloadAction<ProjectSection>) {
            const idx = state.projectSections.findIndex((aProjectSection) => aProjectSection.id === action.payload.id);
            if (idx < 0) {
                throw new Error(`Project Section ${action.payload.id} not found in store`);
            }
            // Merge rather than replace: an update response only carries the fields it
            // actually changed (project-management fields), so fields it's silent on
            // — like overallState, which reflects pavement grade, not project data —
            // must keep their existing value instead of being wiped/overwritten.
            state.projectSections[idx] = { ...state.projectSections[idx], ...action.payload };
        },
        updatedProjectSections(state: State, action: PayloadAction<Array<ProjectSection>>) {
            const incomingById = new Map(action.payload.map((ps) => [ps.id, ps]));
            state.projectSections = state.projectSections.map((existing) => {
                const incoming = incomingById.get(existing.id);

                return incoming ? { ...existing, ...incoming } : existing;
            });
        },
        deselectProjectSection(state: State, action: PayloadAction<ProjectSectionId>) {
            state.selectedProjectSectionIds = state.selectedProjectSectionIds.filter((id) => id !== action.payload);
        },
        paginationUpdated(state: State, action: PayloadAction<ProjectSectionPagination>) {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setSelectedProjectSections(state: State, action: PayloadAction<Array<ProjectSectionId>>) {
            state.selectedProjectSectionIds = action.payload;
        },
        setColumnVisibility(state: State, action: PayloadAction<Record<string, boolean>>) {
            state.columnVisibility = action.payload;
        },
        setCurrentProjectId(state: State, action: PayloadAction<string | null>) {
            state.currentProjectId = action.payload;
        },
        filterUpdated(state: State, action: PayloadAction<ProjectSectionFilter>) {
            state.filter = action.payload;
        },
    },
});

export const {
    resetStarted,
    resetWith,
    setProject,
    selectProjectSection,
    deselectProjectSection,
    setSelectedProjectSections,
    paginationUpdated,
    updatedProjectSection,
    updatedProjectSections,
    setColumnVisibility,
    setCurrentProjectId,
    filterUpdated,
} = projectSlice.actions;
export const projectReducer = projectSlice.reducer;
